import {
  CHECKLIST_ANSWERS,
  CHECKLIST_PHASES,
  OPERATION_NOTE_MAX_SECTION,
  OPERATION_NOTE_SECTIONS,
  THEATRE_CASE_STATUSES,
  THEATRE_URGENCIES,
} from '@wodi/domain';
import { z } from 'zod';

import { ageSchema, isoDaySchema, reasonSchema } from './clinical-common.ts';
import { LAB_GATE_STATES } from './lab.ts';
import { amountSchema } from './money.ts';
import { shaApprovalSchema } from './sha-approval.ts';

// Theatre lite (docs/plans/phase2-theatre.md §3). A case hangs from a visit and a procedure
// order on it; the checklist phases, the operation note and theatre charges hang from the
// case. Only internal IDs travel; names and clinical text never reach a URL.

export type { ChecklistAnswer, ChecklistPhase, TheatreCaseStatus } from '@wodi/domain';
export {
  CHECKLIST_ANSWERS,
  CHECKLIST_PHASES,
  OPERATION_NOTE_SECTIONS,
  THEATRE_CASE_STATUSES,
  THEATRE_URGENCIES,
};

const actorSchema = z.object({ id: z.uuid(), displayName: z.string() });
const name = z.string().trim().min(1).max(80);
const sortOrder = z.number().int().min(0).max(9999);
/** Clinical free text: stored, shown to clinicians, never logged. */
const noteText = z.string().trim().min(1).max(2000);

// --- Settings: theatres and checklist items ------------------------------------------------------

export const adminTheatreSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  status: z.enum(['active', 'inactive']),
  sortOrder: z.number().int(),
  feeItem: z.object({ id: z.uuid(), code: z.string(), name: z.string() }),
});
export type AdminTheatre = z.infer<typeof adminTheatreSchema>;

export const createTheatreBodySchema = z.object({
  name,
  /** An active service of category procedure or other. */
  feeServiceItemId: z.uuid(),
  sortOrder: sortOrder.optional(),
});

export const updateTheatreBodySchema = z
  .object({
    name: name.optional(),
    feeServiceItemId: z.uuid().optional(),
    status: z.enum(['active', 'inactive']).optional(),
    sortOrder: sortOrder.optional(),
  })
  .refine((v) => Object.values(v).some((x) => x !== undefined), 'Nothing to change');

export const checklistItemSchema = z.object({
  id: z.uuid(),
  phase: z.enum(CHECKLIST_PHASES),
  text: z.string(),
  requiresYes: z.boolean(),
  /** Placeholder wording nobody has confirmed (T1). */
  provisional: z.boolean(),
  status: z.enum(['active', 'inactive']),
  sortOrder: z.number().int(),
});
export type ChecklistItemView = z.infer<typeof checklistItemSchema>;

const itemText = z.string().trim().min(1).max(300);

export const createChecklistItemBodySchema = z.object({
  phase: z.enum(CHECKLIST_PHASES),
  text: itemText,
  requiresYes: z.boolean().optional(),
  sortOrder: sortOrder.optional(),
});

/** Saving wording confirms it: the item is no longer a placeholder. */
export const updateChecklistItemBodySchema = z
  .object({
    text: itemText.optional(),
    requiresYes: z.boolean().optional(),
    status: z.enum(['active', 'inactive']).optional(),
    sortOrder: sortOrder.optional(),
  })
  .refine((v) => Object.values(v).some((x) => x !== undefined), 'Nothing to change');

// --- Booking ---------------------------------------------------------------------------------

export const bookCaseBodySchema = z
  .object({
    /** Generated once per booking; a retried request returns the same case. */
    clientRequestId: z.uuid(),
    visitId: z.uuid(),
    theatreId: z.uuid(),
    scheduledStart: z.iso.datetime({ offset: true }),
    expectedMinutes: z.number().int().min(1).max(1440),
    surgeonUserId: z.uuid(),
    urgency: z.enum(THEATRE_URGENCIES),
    /** A requested procedure order on the visit, or a procedure from the catalogue. */
    serviceOrderId: z.uuid().optional(),
    serviceItemId: z.uuid().optional(),
    /** Why the procedure is requested, when a new order is placed. */
    indication: z.string().trim().max(500).optional(),
    bookingNote: z.string().trim().max(1000).optional(),
  })
  .refine((v) => (v.serviceOrderId === undefined) !== (v.serviceItemId === undefined), {
    message: 'Choose a requested procedure or one from the catalogue',
    path: ['serviceItemId'],
  });
export type BookCaseBody = z.infer<typeof bookCaseBodySchema>;

export const rescheduleCaseBodySchema = z.object({
  theatreId: z.uuid(),
  scheduledStart: z.iso.datetime({ offset: true }),
  expectedMinutes: z.number().int().min(1).max(1440),
  reason: reasonSchema,
});

export const cancelCaseBodySchema = z.object({
  reason: reasonSchema,
  /** Also cancel the procedure request (and its charge) when it won't be rebooked. */
  cancelProcedureRequest: z.boolean(),
});

/**
 * A catalogue search for the booking (procedures) and for services used (theatre services),
 * priced for the case's visit. Answers in the procedure search's shape.
 */
export const theatreItemSearchBodySchema = z.object({
  visitId: z.uuid(),
  query: z.string().trim().min(2).max(100),
  purpose: z.enum(['procedure', 'service']),
});

export const surgeonSchema = z.object({ id: z.uuid(), displayName: z.string() });
export const surgeonListSchema = z.array(surgeonSchema);

// --- Checklist phases ------------------------------------------------------------------------

export const recordChecklistBodySchema = z.object({
  /** Clinical time; defaults to now. Not in the future, not before the previous phase. */
  recordedAt: z.iso.datetime({ offset: true }).optional(),
  answers: z
    .array(
      z.object({
        itemId: z.uuid(),
        answer: z.enum(CHECKLIST_ANSWERS),
        note: noteText.nullable(),
      }),
    )
    .max(100),
  note: noteText.optional(),
  /** Pre-op only (T7): a consent-form document on the visit, or why there is none. */
  consentDocumentId: z.uuid().optional(),
  consentNotObtainedReason: reasonSchema.optional(),
});
export type RecordChecklistBody = z.infer<typeof recordChecklistBodySchema>;

export const checklistResponseSchema = z.object({
  itemId: z.uuid(),
  text: z.string(),
  requiresYes: z.boolean(),
  answer: z.enum(CHECKLIST_ANSWERS),
  note: z.string().nullable(),
});

export const checklistPhaseViewSchema = z.object({
  phase: z.enum(CHECKLIST_PHASES),
  recorded: z
    .object({
      at: z.iso.datetime(),
      by: actorSchema,
      responses: z.array(checklistResponseSchema),
      note: z.string().nullable(),
    })
    .nullable(),
  /** The active items to answer, while not recorded. */
  items: z.array(checklistItemSchema),
});
export type ChecklistPhaseView = z.infer<typeof checklistPhaseViewSchema>;

// --- Operation note --------------------------------------------------------------------------

const sectionInput = z.object({
  key: z.enum(OPERATION_NOTE_SECTIONS.map((s) => s.key) as [string, ...string[]]),
  text: z.string().max(OPERATION_NOTE_MAX_SECTION),
});

export const signOperationNoteBodySchema = z.object({
  sections: z.array(sectionInput).max(OPERATION_NOTE_SECTIONS.length),
});

export const amendOperationNoteBodySchema = z.object({
  sections: z.array(sectionInput).max(OPERATION_NOTE_SECTIONS.length),
  reason: reasonSchema,
});

export const operationNoteVersionSchema = z.object({
  version: z.number().int().min(1),
  kind: z.enum(['signed', 'amendment']),
  sections: z.array(z.object({ key: z.string(), heading: z.string(), text: z.string() })),
  changeReason: z.string().nullable(),
  at: z.iso.datetime(),
  by: actorSchema,
});

export const operationNoteSchema = z.object({
  id: z.uuid(),
  author: actorSchema,
  signedAt: z.iso.datetime(),
  currentVersion: z.number().int().min(1),
  /** Newest first. */
  versions: z.array(operationNoteVersionSchema),
});
export type OperationNoteView = z.infer<typeof operationNoteSchema>;

// --- Theatre charges -------------------------------------------------------------------------

export const recordTheatreChargeBodySchema = z.object({
  clientRequestId: z.uuid(),
  /** An active service (not a stock item, T6). */
  serviceItemId: z.uuid(),
  quantity: z.number().int().min(1).max(100),
});

export const cancelTheatreChargeBodySchema = z.object({ reason: reasonSchema });

export const theatreChargeViewSchema = z.object({
  /** The theatre_charges row, or null for the theatre fee (charged from the case). */
  id: z.uuid().nullable(),
  kind: z.enum(['theatre_fee', 'service']),
  item: z.object({ id: z.uuid(), code: z.string(), name: z.string() }),
  quantity: z.number().int(),
  status: z.enum(['active', 'cancelled']),
  cancelReason: z.string().nullable(),
  chargeStatus: z.enum(['pending', 'billed', 'cancelled']).nullable(),
  total: amountSchema.nullable(),
  recordedAt: z.iso.datetime(),
  recordedBy: actorSchema,
});
export type TheatreChargeView = z.infer<typeof theatreChargeViewSchema>;

export const cancelTheatreChargeResultSchema = z.object({
  charge: z.enum(['cancelled', 'needs_credit_note', 'nothing_to_cancel']),
});

// --- The case --------------------------------------------------------------------------------

export const theatreCaseSchema = z.object({
  id: z.uuid(),
  patientId: z.uuid(),
  visit: z.object({
    id: z.uuid(),
    visitNumber: z.string(),
    type: z.string(),
    status: z.string(),
    /** The stay, when the case is on an inpatient visit. */
    admissionId: z.uuid().nullable(),
  }),
  theatre: z.object({ id: z.uuid(), name: z.string() }),
  procedure: z.object({
    serviceOrderId: z.uuid(),
    name: z.string(),
    code: z.string(),
    orderStatus: z.string(),
  }),
  surgeon: actorSchema,
  urgency: z.enum(THEATRE_URGENCIES),
  scheduledStart: z.iso.datetime(),
  expectedMinutes: z.number().int(),
  bookingNote: z.string().nullable(),
  bookedBy: actorSchema,
  bookedAt: z.iso.datetime(),
  status: z.enum(THEATRE_CASE_STATUSES),
  startedAt: z.iso.datetime().nullable(),
  endedAt: z.iso.datetime().nullable(),
  cancelReason: z.string().nullable(),
  /** Whether the procedure can be done yet, in the lab's words. */
  gate: z.enum(LAB_GATE_STATES),
  approval: shaApprovalSchema.nullable(),
  consent: z
    .object({
      documentId: z.uuid().nullable(),
      documentTitle: z.string().nullable(),
      notObtainedReason: z.string().nullable(),
    })
    .nullable(),
  /** Active consent forms on the case's visit, for the pre-op picker. */
  consentForms: z.array(
    z.object({ id: z.uuid(), title: z.string(), uploadedAt: z.iso.datetime() }),
  ),
  checklist: z.array(checklistPhaseViewSchema),
  operationNote: operationNoteSchema.nullable(),
  charges: z.array(theatreChargeViewSchema),
});
export type TheatreCaseView = z.infer<typeof theatreCaseSchema>;

// --- The theatre list -----------------------------------------------------------------------

export const listCaseSchema = z.object({
  id: z.uuid(),
  scheduledStart: z.iso.datetime(),
  expectedMinutes: z.number().int(),
  status: z.enum(THEATRE_CASE_STATUSES),
  urgency: z.enum(THEATRE_URGENCIES),
  /** Null with `patient`: the procedure says what the care is, so it is masked too. */
  procedureName: z.string().nullable(),
  surgeon: actorSchema,
  /** Null when the reader may not see this patient (a labelled visit). */
  patient: z
    .object({
      id: z.uuid(),
      displayName: z.string(),
      patientNumber: z.string(),
      sex: z.string(),
      age: ageSchema.nullable(),
    })
    .nullable(),
  /** The phases recorded so far, in order. */
  phasesDone: z.array(z.enum(CHECKLIST_PHASES)),
  noteSigned: z.boolean(),
});
export type TheatreListCase = z.infer<typeof listCaseSchema>;

export const theatreListSchema = z.object({
  date: isoDaySchema,
  theatres: z.array(
    z.object({
      id: z.uuid(),
      name: z.string(),
      cases: z.array(listCaseSchema),
    }),
  ),
});
export type TheatreList = z.infer<typeof theatreListSchema>;

export const theatreListQuerySchema = z.object({ date: isoDaySchema.optional() });

/** A visit's cases, for the stay and consultation pages. IDs, times and states only. */
export const visitTheatreCasesSchema = z.array(
  z.object({
    id: z.uuid(),
    scheduledStart: z.iso.datetime(),
    status: z.enum(THEATRE_CASE_STATUSES),
    procedureName: z.string(),
    theatreName: z.string(),
  }),
);

/** Requested procedure orders on a visit with no open case yet, for the booking page. */
export const bookableProceduresSchema = z.array(
  z.object({
    serviceOrderId: z.uuid(),
    name: z.string(),
    code: z.string(),
    orderedAt: z.iso.datetime(),
  }),
);
